import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Check,
  ChevronDown,
  CircleAlert,
  Compass,
  Database,
  Eye,
  Flag,
  Layers,
  LineChart,
  Lock,
  Rocket,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Workflow,
  X,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../../hooks/usePageMeta';
import { EARLY_ACCESS_BADGE, MARKETING_CTA } from './marketingConfig';

// ─── Static data ──────────────────────────────────────────────────────────────

const lifecycleStages = [
  { step: 'Ingest', state: 'done' },
  { step: 'Analyze', state: 'done' },
  { step: 'Validate', state: 'gate' },
  { step: 'Score', state: 'done' },
  { step: 'Deliver', state: 'active' },
  { step: 'Audit', state: 'pending' },
] as const;

const telemetryReadout = [
  { metric: 'Market drift', value: '+0.42σ', tone: 'text-emerald-300' },
  { metric: 'Signal latency', value: '142ms', tone: 'text-sky-200' },
  { metric: 'Live feeds', value: '12/12', tone: 'text-gray-200' },
];

const commandTimeline = [
  ['Model ingest', '03:14:08 UTC'],
  ['Validation lock', '03:14:12 UTC'],
  ['Risk checkpoint', '03:14:17 UTC'],
  ['Release orchestration', '03:14:21 UTC'],
];

const ecosystemModules = [
  {
    product: 'DiamondEdge',
    sport: 'MLB',
    status: 'Live',
    emoji: '⚾',
    accent: 'border-sky-400/40 bg-sky-400/10',
    statusColor: 'border-sky-400/40 bg-sky-400/10 text-sky-300',
    to: '/products/diamondedge',
  },
  {
    product: 'GridironEdge',
    sport: 'NFL',
    status: 'Roadmap',
    emoji: '🏈',
    accent: 'border-emerald-400/40 bg-emerald-400/10',
    statusColor: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
    to: '/products/gridironedge',
  },
  {
    product: 'CourtEdge',
    sport: 'NBA / WNBA',
    status: 'Roadmap',
    emoji: '🏀',
    accent: 'border-orange-400/40 bg-orange-400/10',
    statusColor: 'border-orange-400/40 bg-orange-400/10 text-orange-300',
    to: '/products/courtedge',
  },
  {
    product: 'IceEdge',
    sport: 'NHL',
    status: 'Roadmap',
    emoji: '🏒',
    accent: 'border-cyan-400/40 bg-cyan-400/10',
    statusColor: 'border-cyan-400/40 bg-cyan-400/10 text-cyan-300',
    to: '/products/iceedge',
  },
  {
    product: 'PitchEdge',
    sport: 'Soccer',
    status: 'Roadmap',
    emoji: '⚽',
    accent: 'border-lime-400/40 bg-lime-400/10',
    statusColor: 'border-lime-400/40 bg-lime-400/10 text-lime-300',
    to: '/products/pitchedge',
  },
];

const trustPillars = [
  {
    title: 'Evidence',
    icon: Eye,
    points: ['Recommendation evidence cards', 'Reasoning for and against each position'],
  },
  {
    title: 'Validation',
    icon: ScanSearch,
    points: ['Gate checks before release', 'Blocked recommendation audit logs'],
  },
  {
    title: 'Uncertainty Disclosure',
    icon: CircleAlert,
    points: ['Confidence calibration context', 'Volatility and disagreement flags'],
  },
  {
    title: 'Risk Controls',
    icon: Compass,
    points: ['Portfolio exposure limits', 'Slate-level warning and throttle rules'],
  },
];

const faqs = [
  {
    q: 'How does Navyrx Sports work?',
    a: 'Navyrx Sports orchestrates specialized AI agents, validates outputs, and surfaces explainable recommendations with risk controls.',
  },
  {
    q: 'What sports are supported?',
    a: 'DiamondEdge (MLB) is live now. GridironEdge (NFL), CourtEdge (NBA/WNBA), IceEdge (NHL), and PitchEdge (Soccer) are on the roadmap — each module passes validation gates before rollout.',
  },
  {
    q: 'Does Navyrx Sports guarantee wins?',
    a: 'No. Navyrx Sports is a decision-support platform focused on disciplined process, transparency, and risk management — not guarantees.',
  },
  {
    q: 'What makes Navyrx Sports different?',
    a: 'Every recommendation ships with evidence, confidence context, and uncertainty disclosure, including blocked recommendation scenarios. The blocks are as important as the releases.',
  },
  {
    q: 'How are recommendations blocked and why does that matter?',
    a: 'When market instability, agent disagreement, injury uncertainty, or portfolio exposure exceeds configured thresholds, the system issues a BLOCK. The recommendation never reaches you — protecting against plays that look good on paper but carry hidden risk. This season, 847 recommendations were blocked before reaching users.',
  },
  {
    q: 'What data sources does Navyrx use?',
    a: 'Navyrx ingests from the MLB Stats API (game data, lineups, pitchers), The Odds API (live lines from 20+ books), and Open-Meteo (weather and park conditions). All data is validated for freshness at ingest time — stale data triggers an automatic DataIntegrity block.',
  },
];

// ─── Simulated live slate ──────────────────────────────────────────────────────

interface SimRec {
  id: string;
  game: string;
  pick: string;
  edgeBase: number;
  confidenceBase: number;
  validation: 'PASSED' | 'WATCH' | 'BLOCKED';
  risk: 'LOW' | 'MEDIUM' | 'HIGH';
  marketDriftBase: number;
  sharpMoney: boolean;
  injuryImpact: 'LOW' | 'MEDIUM' | 'HIGH';
  status: 'RELEASED' | 'REVIEWING' | 'BLOCKED';
}

const initialSlate: SimRec[] = [
  {
    id: 'nyy-bos',
    game: 'Yankees @ Red Sox',
    pick: 'NYY ML',
    edgeBase: 4.2,
    confidenceBase: 78,
    validation: 'PASSED',
    risk: 'MEDIUM',
    marketDriftBase: 0.7,
    sharpMoney: true,
    injuryImpact: 'LOW',
    status: 'RELEASED',
  },
  {
    id: 'lad-sf',
    game: 'Dodgers @ Giants',
    pick: 'LAD -1.5',
    edgeBase: 2.8,
    confidenceBase: 61,
    validation: 'WATCH',
    risk: 'MEDIUM',
    marketDriftBase: -1.2,
    sharpMoney: false,
    injuryImpact: 'MEDIUM',
    status: 'REVIEWING',
  },
  {
    id: 'nym-atl',
    game: 'Mets @ Braves',
    pick: 'NYM ML',
    edgeBase: -0.3,
    confidenceBase: 44,
    validation: 'BLOCKED',
    risk: 'HIGH',
    marketDriftBase: 2.1,
    sharpMoney: false,
    injuryImpact: 'HIGH',
    status: 'BLOCKED',
  },
];

const blockReasons = [
  { label: 'Market instability', detail: 'Line moved 2.1σ in 18 min — exceeds drift threshold' },
  { label: 'Confidence decay', detail: 'Agent confidence fell from 61 → 44 post-ingest recompute' },
  { label: 'Injury uncertainty', detail: 'Starting pitcher status unresolved at lock time' },
  { label: 'Agent disagreement', detail: 'Risk agent issued BLOCK_WARNING; 3/5 agents non-consensus' },
  { label: 'Exposure conflict', detail: 'Portfolio already at prime-slate cap — additional unit blocked' },
];

const agentNodes = [
  { name: 'Statistical agent', signal: +0.21, state: 'AGREE' as const, tone: 'text-emerald-300' },
  { name: 'Injury agent', signal: -0.18, state: 'DISAGREE' as const, tone: 'text-amber-300' },
  { name: 'Market agent', signal: +0.09, state: 'AGREE' as const, tone: 'text-emerald-300' },
  { name: 'Risk agent', signal: 0, state: 'BLOCK' as const, tone: 'text-red-300' },
  { name: 'Portfolio agent', signal: -0.06, state: 'DISAGREE' as const, tone: 'text-amber-300' },
];

const institutionalKPIs = [
  { label: 'Validation pass rate', value: '94.2%', sub: 'Season average', icon: ShieldCheck, tone: 'text-emerald-300' },
  { label: 'Recs blocked (season)', value: '847', sub: 'Governance interventions', icon: Lock, tone: 'text-red-300' },
  { label: 'Confidence calibration', value: '±3.1%', sub: 'Mean absolute error', icon: Target, tone: 'text-sky-300' },
  { label: 'Signal latency avg', value: '142ms', sub: 'End-to-end ingest', icon: Zap, tone: 'text-blue-300' },
  { label: 'Consensus stability', value: '89.4%', sub: 'Agent agreement rate', icon: Users, tone: 'text-indigo-300' },
  { label: 'Drift response time', value: '6.3s', sub: 'Market alert to recompute', icon: Activity, tone: 'text-cyan-300' },
];

const sectionMotion = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

// ─── New section data ──────────────────────────────────────────────────────────

const tickerItems = [
  { label: 'Validation pass rate', value: '94.2%', color: 'text-emerald-300' },
  { label: 'Recs blocked (season)', value: '847', color: 'text-red-300' },
  { label: 'Signal latency avg', value: '142ms', color: 'text-sky-300' },
  { label: 'Consensus stability', value: '89.4%', color: 'text-indigo-300' },
  { label: 'Confidence calibration', value: '±3.1%', color: 'text-amber-300' },
  { label: 'Drift response time', value: '6.3s', color: 'text-cyan-300' },
  { label: 'Live data feeds', value: '12/12', color: 'text-emerald-300' },
  { label: 'Agent agreement rate', value: '89.4%', color: 'text-blue-300' },
];

const agentsData = [
  {
    name: 'Matchup Agent',
    weight: 25,
    icon: Target,
    textColor: 'text-emerald-300',
    borderColor: 'border-emerald-400/30',
    bgColor: 'bg-emerald-400/5',
    signal: '+0.31',
    state: 'AGREE',
    stateTone: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
    signalTone: 'text-emerald-300',
    desc: 'Pitcher vs. lineup dynamics and day-of starters',
  },
  {
    name: 'Market Agent',
    weight: 20,
    icon: LineChart,
    textColor: 'text-sky-300',
    borderColor: 'border-sky-400/30',
    bgColor: 'bg-sky-400/5',
    signal: '+0.21',
    state: 'AGREE',
    stateTone: 'border-sky-400/30 bg-sky-400/10 text-sky-300',
    signalTone: 'text-sky-300',
    desc: 'Odds efficiency, vig, and sharp money signals',
  },
  {
    name: 'Risk Agent',
    weight: 15,
    icon: AlertTriangle,
    textColor: 'text-red-300',
    borderColor: 'border-red-400/30',
    bgColor: 'bg-red-400/5',
    signal: '0.00',
    state: 'BLOCK',
    stateTone: 'border-red-400/30 bg-red-400/10 text-red-300',
    signalTone: 'text-red-300',
    desc: 'Bankroll exposure, Kelly sizing, and violations',
  },
  {
    name: 'Portfolio Agent',
    weight: 15,
    icon: Layers,
    textColor: 'text-indigo-300',
    borderColor: 'border-indigo-400/30',
    bgColor: 'bg-indigo-400/5',
    signal: '-0.06',
    state: 'DISAGREE',
    stateTone: 'border-amber-400/30 bg-amber-400/10 text-amber-300',
    signalTone: 'text-amber-300',
    desc: 'Diversification and slate-level correlation',
  },
  {
    name: 'Form Agent',
    weight: 15,
    icon: Activity,
    textColor: 'text-blue-300',
    borderColor: 'border-blue-400/30',
    bgColor: 'bg-blue-400/5',
    signal: '+0.14',
    state: 'AGREE',
    stateTone: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
    signalTone: 'text-emerald-300',
    desc: 'Last-10 records, streaks, and run differential',
  },
  {
    name: 'Context Agent',
    weight: 10,
    icon: Compass,
    textColor: 'text-amber-300',
    borderColor: 'border-amber-400/30',
    bgColor: 'bg-amber-400/5',
    signal: '+0.08',
    state: 'AGREE',
    stateTone: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
    signalTone: 'text-emerald-300',
    desc: 'Weather, park factors, and home-field advantage',
  },
  {
    name: 'Validation Agent',
    weight: null as null,
    icon: ScanSearch,
    textColor: 'text-cyan-300',
    borderColor: 'border-cyan-400/30',
    bgColor: 'bg-cyan-400/5',
    signal: '-0.18',
    state: 'CHALLENGE',
    stateTone: 'border-cyan-400/30 bg-cyan-400/10 text-cyan-300',
    signalTone: 'text-cyan-300',
    desc: 'Issues adversarial challenges before every release',
  },
  {
    name: 'Data Integrity',
    weight: null as null,
    icon: Database,
    textColor: 'text-purple-300',
    borderColor: 'border-purple-400/30',
    bgColor: 'bg-purple-400/5',
    signal: 'GATE',
    state: 'GATE',
    stateTone: 'border-purple-400/30 bg-purple-400/10 text-purple-300',
    signalTone: 'text-purple-300',
    desc: 'Blocks recs when data is stale or incomplete',
  },
];

const problemRows = [
  { old: 'Gut picks and hot takes', nav: '8-agent parallel analysis' },
  { old: 'No reasoning, no audit trail', nav: 'Full evidence packet per pick' },
  { old: 'Bad picks slip through undetected', nav: '847 recommendations blocked this season' },
  { old: 'Black-box confidence scores', nav: 'Calibrated ±3.1% accuracy disclosed' },
  { old: 'One bad slate wipes your gains', nav: 'Portfolio exposure caps enforced' },
  { old: '"Just trust us"', nav: 'Validation gate before every release' },
];

const heroStatValues = [
  { value: 94.2, decimals: 1, prefix: '', suffix: '%', label: 'Validation pass rate', color: 'text-emerald-300' },
  { value: 847, decimals: 0, prefix: '', suffix: '', label: 'Recs blocked this season', color: 'text-red-300' },
  { value: 142, decimals: 0, prefix: '', suffix: 'ms', label: 'Signal latency avg', color: 'text-sky-300' },
];

const kpiAnimationValues = [
  { raw: 94.2, decimals: 1, prefix: '', suffix: '%' },
  { raw: 847, decimals: 0, prefix: '', suffix: '' },
  { raw: 3.1, decimals: 1, prefix: '±', suffix: '%' },
  { raw: 142, decimals: 0, prefix: '', suffix: 'ms' },
  { raw: 89.4, decimals: 1, prefix: '', suffix: '%' },
  { raw: 6.3, decimals: 1, prefix: '', suffix: 's' },
];

const howItWorksSteps = [
  {
    num: '01',
    title: 'Data Streams In',
    desc: 'Live odds, pitcher lineups, weather, and market signals ingest on a continuous cycle from MLB Stats API, The Odds API, and Open-Meteo.',
    icon: Database,
    color: 'text-sky-300',
    borderColor: 'border-sky-400/30',
    bgColor: 'bg-sky-400/8',
  },
  {
    num: '02',
    title: '8 Agents Analyze in Parallel',
    desc: "Each agent scores independently — no shared state, no groupthink. Consensus is computed from honest disagreement. A Validation Agent issues adversarial challenges to every output.",
    icon: Activity,
    color: 'text-indigo-300',
    borderColor: 'border-indigo-400/30',
    bgColor: 'bg-indigo-400/8',
  },
  {
    num: '03',
    title: 'Validated or Blocked',
    desc: 'The Validation Gate runs evidence, uncertainty, and risk exposure checks. Every pick is released or blocked with a full audit trail — no exceptions, no silent passes.',
    icon: ShieldCheck,
    color: 'text-emerald-300',
    borderColor: 'border-emerald-400/30',
    bgColor: 'bg-emerald-400/8',
  },
];

// ─── Animation constants ───────────────────────────────────────────────────────

const LIVE_TILE_COUNT = 2;
const FIELD_ROTATION_COUNT = 3;
const PULSE_ANIMATION_INTERVAL_MS = 3200;
const CONFIDENCE_DELTA_MULTIPLIER = 2;
const EDGE_DELTA_MULTIPLIER = 0.3;
const DRIFT_DELTA_MULTIPLIER = 0.15;
const CONSENSUS_ANIMATION_INTERVAL_MS = 1400;
const CONSENSUS_SCORE_UPPER = 68;
const CONSENSUS_SCORE_LOWER = 58;

// ─── Existing sub-components (unchanged) ─────────────────────────────────────

interface LiveRecState {
  id: string;
  confidence: number;
  edge: number;
  marketDrift: number;
  lastUpdated: 'confidence' | 'edge' | 'drift' | null;
}

function LivePulseBoard({ disableMotion }: { disableMotion: boolean }) {
  const [liveStates, setLiveStates] = useState<LiveRecState[]>(
    initialSlate.map((r) => ({
      id: r.id,
      confidence: r.confidenceBase,
      edge: r.edgeBase,
      marketDrift: r.marketDriftBase,
      lastUpdated: null,
    })),
  );
  const [expandedBlock, setExpandedBlock] = useState(false);
  const [flashId, setFlashId] = useState<string | null>(null);
  const tickRef = useRef(0);

  const nudge = useCallback(() => {
    tickRef.current += 1;
    const tick = tickRef.current;
    const targetIdx = tick % LIVE_TILE_COUNT;
    const field: 'confidence' | 'edge' | 'drift' =
      tick % FIELD_ROTATION_COUNT === 0 ? 'confidence' : tick % FIELD_ROTATION_COUNT === 1 ? 'edge' : 'drift';
    setLiveStates((prev) =>
      prev.map((s, i) => {
        if (i !== targetIdx) return { ...s, lastUpdated: null };
        const multiplier =
          field === 'confidence' ? CONFIDENCE_DELTA_MULTIPLIER : field === 'edge' ? EDGE_DELTA_MULTIPLIER : DRIFT_DELTA_MULTIPLIER;
        const delta = (Math.random() * 2 - 1) * multiplier;
        return {
          ...s,
          confidence: field === 'confidence' ? Math.max(30, Math.min(95, Math.round(s.confidence + delta))) : s.confidence,
          edge: field === 'edge' ? Math.round((s.edge + delta) * 10) / 10 : s.edge,
          marketDrift: field === 'drift' ? Math.round((s.marketDrift + delta) * 100) / 100 : s.marketDrift,
          lastUpdated: field === 'confidence' ? 'confidence' : field === 'edge' ? 'edge' : 'drift',
        };
      }),
    );
    setFlashId(initialSlate[targetIdx].id);
    setTimeout(() => setFlashId(null), 600);
  }, []);

  useEffect(() => {
    if (disableMotion) return;
    const id = setInterval(nudge, PULSE_ANIMATION_INTERVAL_MS);
    return () => clearInterval(id);
  }, [disableMotion, nudge]);

  return (
    <div className="space-y-3" data-testid="live-pulse-board">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide text-gray-500">Live slate · simulated intelligence preview</p>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/30 bg-sky-400/10 px-2 py-0.5 text-[10px] text-sky-300">
          <span className="signal-pulse relative inline-block h-1.5 w-1.5 rounded-full bg-sky-400" />
          Pulse active
        </span>
      </div>

      {initialSlate.map((rec, i) => {
        const live = liveStates[i];
        const isFlashing = flashId === rec.id && !disableMotion;
        return (
          <div
            key={rec.id}
            className={[
              'rounded-xl border p-4 transition-all duration-500',
              rec.status === 'RELEASED'
                ? 'border-emerald-400/30 bg-emerald-400/5'
                : rec.status === 'BLOCKED'
                  ? 'border-red-400/30 bg-red-400/5'
                  : 'border-sky-400/20 bg-sky-400/5',
              isFlashing ? 'border-sky-300/50 bg-sky-300/10' : '',
            ].join(' ')}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[11px] text-gray-400">{rec.game}</p>
                <p className="mt-0.5 text-sm font-semibold">{rec.pick}</p>
              </div>
              <span
                className={[
                  'shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                  rec.status === 'RELEASED'
                    ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300'
                    : rec.status === 'BLOCKED'
                      ? 'border-red-400/40 bg-red-400/10 text-red-300'
                      : 'border-sky-400/30 bg-sky-400/10 text-sky-300',
                ].join(' ')}
              >
                {rec.status === 'REVIEWING' ? 'Reviewing' : rec.status === 'RELEASED' ? '✓ Released' : '✕ Blocked'}
              </span>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded border border-white/10 bg-slate-950/60 px-2 py-1.5">
                <p className="text-gray-500">Edge</p>
                <p
                  className={[
                    'mt-0.5 font-medium tabular-nums transition-colors duration-500',
                    live.edge > 0 ? 'text-emerald-300' : 'text-red-300',
                    live.lastUpdated === 'edge' && isFlashing ? 'text-sky-200' : '',
                  ].join(' ')}
                >
                  {live.edge > 0 ? '+' : ''}
                  {live.edge.toFixed(1)}%
                </p>
              </div>
              <div className="rounded border border-white/10 bg-slate-950/60 px-2 py-1.5">
                <p className="text-gray-500">Confidence</p>
                <p
                  className={[
                    'mt-0.5 font-medium tabular-nums transition-colors duration-500',
                    live.confidence >= 70 ? 'text-emerald-300' : live.confidence >= 55 ? 'text-amber-300' : 'text-red-300',
                    live.lastUpdated === 'confidence' && isFlashing ? 'text-sky-200' : '',
                  ].join(' ')}
                >
                  {live.confidence}
                </p>
              </div>
              <div className="rounded border border-white/10 bg-slate-950/60 px-2 py-1.5">
                <p className="text-gray-500">Mkt drift</p>
                <p
                  className={[
                    'mt-0.5 font-medium tabular-nums transition-colors duration-500',
                    Math.abs(live.marketDrift) > 1.5 ? 'text-red-300' : 'text-sky-200',
                    live.lastUpdated === 'drift' && isFlashing ? 'text-white' : '',
                  ].join(' ')}
                >
                  {live.marketDrift > 0 ? '+' : ''}
                  {live.marketDrift.toFixed(2)}σ
                </p>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-gray-500">
              <span>
                Risk:{' '}
                <span className={rec.risk === 'HIGH' ? 'text-red-300' : rec.risk === 'MEDIUM' ? 'text-amber-300' : 'text-emerald-300'}>
                  {rec.risk}
                </span>
              </span>
              <span>
                Sharp: <span className={rec.sharpMoney ? 'text-emerald-300' : 'text-gray-400'}>{rec.sharpMoney ? 'YES' : 'NO'}</span>
              </span>
              <span>
                Injury:{' '}
                <span className={rec.injuryImpact === 'HIGH' ? 'text-red-300' : rec.injuryImpact === 'MEDIUM' ? 'text-amber-300' : 'text-gray-400'}>
                  {rec.injuryImpact}
                </span>
              </span>
              <span>
                Validation:{' '}
                <span
                  className={
                    rec.validation === 'PASSED' ? 'text-emerald-300' : rec.validation === 'WATCH' ? 'text-amber-300' : 'text-red-300'
                  }
                >
                  {rec.validation}
                </span>
              </span>
            </div>

            {rec.status === 'BLOCKED' && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setExpandedBlock((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded border border-red-400/30 bg-red-400/8 px-3 py-1.5 text-[11px] text-red-300 transition-colors hover:bg-red-400/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                  aria-expanded={expandedBlock}
                  aria-controls="block-reason-panel"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Why was this blocked?
                  <ChevronDown
                    className={['h-3 w-3 transition-transform duration-200', expandedBlock ? 'rotate-180' : ''].join(' ')}
                  />
                </button>

                {expandedBlock && (
                  <div
                    id="block-reason-panel"
                    className="mt-2 space-y-1.5 rounded-lg border border-red-400/20 bg-red-900/20 p-3"
                    role="region"
                    aria-label="Block reason details"
                  >
                    <p className="text-[10px] uppercase tracking-wide text-red-400/70">Validation failure audit</p>
                    {blockReasons.map((r) => (
                      <div key={r.label} className="flex gap-2 text-[11px]">
                        <span className="mt-0.5 text-red-400">✕</span>
                        <div>
                          <p className="font-medium text-red-200">{r.label}</p>
                          <p className="text-red-300/70">{r.detail}</p>
                        </div>
                      </div>
                    ))}
                    <p className="mt-2 border-t border-red-400/20 pt-2 text-[10px] text-red-400/60">
                      Recommendation quarantined — will reprocess on next ingest cycle.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AgentOrchestrationPanel({ disableMotion }: { disableMotion: boolean }) {
  const [consensusScore, setConsensusScore] = useState(62);
  const directionRef = useRef(1);

  useEffect(() => {
    if (disableMotion) return;
    const id = setInterval(() => {
      setConsensusScore((v) => {
        const next = v + directionRef.current;
        if (next >= CONSENSUS_SCORE_UPPER || next <= CONSENSUS_SCORE_LOWER) directionRef.current *= -1;
        return next;
      });
    }, CONSENSUS_ANIMATION_INTERVAL_MS);
    return () => clearInterval(id);
  }, [disableMotion]);

  return (
    <div className="space-y-3" data-testid="agent-orchestration-panel">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">Multi-agent orchestration</p>
      <div className="space-y-2">
        {agentNodes.map((agent) => (
          <div key={agent.name} className="flex items-center gap-3 rounded-lg border border-white/10 bg-slate-950/60 px-3 py-2 text-xs">
            <span
              className={[
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[9px] font-bold',
                agent.state === 'AGREE'
                  ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300'
                  : agent.state === 'BLOCK'
                    ? 'border-red-400/50 bg-red-400/15 text-red-300'
                    : 'border-amber-400/40 bg-amber-400/10 text-amber-300',
              ].join(' ')}
              aria-label={agent.state}
            >
              {agent.state === 'AGREE' ? '✓' : agent.state === 'BLOCK' ? '!' : '⚠'}
            </span>
            <span className="flex-1 text-gray-300">{agent.name}</span>
            {agent.state === 'BLOCK' ? (
              <span className="font-medium text-red-300">BLOCK WARNING</span>
            ) : (
              <span className={['font-medium tabular-nums', agent.tone].join(' ')}>
                {agent.signal > 0 ? '+' : ''}
                {agent.signal.toFixed(2)}
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-sky-400/25 bg-sky-400/8 px-4 py-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">Consensus core</span>
          <span
            className={[
              'font-semibold tabular-nums transition-colors duration-700',
              consensusScore >= 65 ? 'text-emerald-300' : consensusScore >= 60 ? 'text-amber-300' : 'text-red-300',
            ].join(' ')}
          >
            CONFIDENCE {consensusScore}
          </span>
        </div>
        <p className="mt-1 text-[10px] text-sky-300/60">2 agents non-consensus · Risk agent override active · Confidence reduced</p>
      </div>
    </div>
  );
}

function PulseCoreVisual({ disableMotion }: { disableMotion: boolean }) {
  return (
    <div className="flex items-center gap-4" aria-hidden data-testid="pulse-core-visual">
      <div className="relative h-[140px] w-[140px] shrink-0">
        <div className="absolute inset-4 rounded-full bg-sky-500/15 blur-2xl" />
        <svg viewBox="0 0 140 140" fill="none" className="absolute inset-0 h-full w-full">
          <motion.g
            animate={disableMotion ? undefined : { rotate: 360 }}
            transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: 'center' }}
          >
            <circle cx="70" cy="70" r="64" stroke="rgba(56,189,248,0.09)" strokeWidth="1" />
            <circle cx="70" cy="6" r="2.5" fill="rgba(56,189,248,0.55)" />
            <circle cx="134" cy="70" r="2" fill="rgba(99,102,241,0.50)" />
            <circle cx="70" cy="134" r="2" fill="rgba(56,189,248,0.45)" />
            <circle cx="6" cy="70" r="2.5" fill="rgba(56,189,248,0.60)" />
            <circle cx="115" cy="25" r="1.5" fill="rgba(56,189,248,0.30)" />
            <circle cx="115" cy="115" r="1.5" fill="rgba(99,102,241,0.28)" />
            <circle cx="25" cy="115" r="1.5" fill="rgba(56,189,248,0.25)" />
            <circle cx="25" cy="25" r="1.5" fill="rgba(56,189,248,0.22)" />
          </motion.g>
          <motion.g
            animate={disableMotion ? undefined : { rotate: -360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: 'center' }}
          >
            <circle cx="70" cy="70" r="48" stroke="rgba(56,189,248,0.14)" strokeWidth="1" />
            <circle cx="70" cy="22" r="2" fill="rgba(16,185,129,0.60)" />
            <circle cx="118" cy="70" r="2" fill="rgba(56,189,248,0.50)" />
            <circle cx="70" cy="118" r="1.5" fill="rgba(16,185,129,0.45)" />
            <circle cx="22" cy="70" r="2" fill="rgba(99,102,241,0.55)" />
          </motion.g>
          <circle cx="70" cy="70" r="30" stroke="rgba(56,189,248,0.20)" strokeWidth="0.75" strokeDasharray="3 7" />
          {!disableMotion && (
            <motion.g
              animate={{ rotate: 360 }}
              transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
              style={{ transformOrigin: 'center' }}
            >
              <line x1="70" y1="70" x2="70" y2="6" stroke="rgba(56,189,248,0.30)" strokeWidth="1" strokeLinecap="round" />
              <line x1="70" y1="70" x2="70" y2="6" stroke="rgba(56,189,248,0.06)" strokeWidth="14" strokeLinecap="round" />
            </motion.g>
          )}
          <motion.circle
            cx="70"
            cy="70"
            r="16"
            fill="rgba(56,189,248,0.07)"
            stroke="rgba(56,189,248,0.28)"
            strokeWidth="1"
            animate={disableMotion ? undefined : { r: [16, 18, 16], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <circle cx="70" cy="70" r="7" fill="rgba(56,189,248,0.85)" />
          <circle cx="70" cy="70" r="3.5" fill="rgba(255,255,255,0.95)" />
          <line x1="70" y1="52" x2="70" y2="42" stroke="rgba(56,189,248,0.18)" strokeWidth="0.5" />
          <line x1="88" y1="70" x2="98" y2="70" stroke="rgba(56,189,248,0.18)" strokeWidth="0.5" />
          <line x1="70" y1="88" x2="70" y2="98" stroke="rgba(56,189,248,0.18)" strokeWidth="0.5" />
          <line x1="52" y1="70" x2="42" y2="70" stroke="rgba(56,189,248,0.18)" strokeWidth="0.5" />
        </svg>
      </div>
      <div className="flex flex-col gap-2">
        <motion.div
          animate={disableMotion ? undefined : { opacity: [0.75, 1, 0.75] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
          className="flex items-center gap-1.5 rounded-md border border-sky-400/25 bg-slate-900/85 px-2.5 py-1.5 text-[10px] backdrop-blur-sm"
        >
          <span className="signal-pulse relative inline-block h-1.5 w-1.5 rounded-full bg-sky-400" />
          <span className="text-sky-300">Consensus core active</span>
        </motion.div>
        <div className="flex items-center gap-1.5 rounded-md border border-emerald-400/20 bg-slate-900/80 px-2.5 py-1.5 text-[10px] backdrop-blur-sm">
          <span className="text-emerald-400">✓</span>
          <span className="text-emerald-300/80">Validation gate passed</span>
        </div>
        <motion.div
          animate={disableMotion ? undefined : { opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
          className="rounded-md border border-white/8 bg-slate-900/70 px-2.5 py-1.5 text-[10px] text-gray-500 backdrop-blur-sm"
        >
          Δ +0.42σ market drift
        </motion.div>
        <div className="rounded-md border border-indigo-400/20 bg-slate-900/70 px-2.5 py-1.5 text-[10px] text-indigo-300/70 backdrop-blur-sm">
          5 agents · 2 non-consensus
        </div>
      </div>
    </div>
  );
}

const riskProfiles = ['Conservative', 'Balanced', 'Aggressive'] as const;
type RiskProfile = (typeof riskProfiles)[number];

function SimulationModePanel({ disableMotion }: { disableMotion: boolean }) {
  const [riskProfile, setRiskProfile] = useState<RiskProfile>('Balanced');
  const [validationStrictness, setValidationStrictness] = useState(70);
  const [exposureCap, setExposureCap] = useState(3);

  const recVolume = riskProfile === 'Conservative' ? 3 : riskProfile === 'Balanced' ? 6 : 9;
  const confidenceThreshold = validationStrictness;
  const blockRate = riskProfile === 'Conservative' ? 38 : riskProfile === 'Balanced' ? 22 : 11;
  const passRate = 100 - blockRate;

  return (
    <div className="mt-6 rounded-2xl border border-surface-4 bg-surface-1 p-6" data-testid="simulation-mode-panel">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-5">
          <div>
            <p className="text-xs font-medium text-gray-400">Risk profile</p>
            <div className="mt-2 flex gap-2">
              {riskProfiles.map((profile) => (
                <button
                  key={profile}
                  type="button"
                  onClick={() => setRiskProfile(profile)}
                  className={[
                    'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                    riskProfile === profile
                      ? 'border-brand/50 bg-brand/15 text-sky-200'
                      : 'border-surface-4 bg-surface-2 text-gray-400 hover:border-brand/30 hover:text-gray-300',
                  ].join(' ')}
                >
                  {profile}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-400">Validation strictness</p>
              <span className="text-xs font-semibold tabular-nums text-sky-300">{validationStrictness}</span>
            </div>
            <input
              type="range"
              min={40}
              max={95}
              value={validationStrictness}
              onChange={(e) => setValidationStrictness(Number(e.target.value))}
              className="mt-2 w-full accent-sky-400"
              aria-label="Validation strictness"
            />
            <div className="mt-1 flex justify-between text-[10px] text-gray-600">
              <span>Permissive</span>
              <span>Strict</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-400">Portfolio exposure cap</p>
              <span className="text-xs font-semibold tabular-nums text-sky-300">{exposureCap.toFixed(1)}u</span>
            </div>
            <input
              type="range"
              min={1}
              max={6}
              step={0.5}
              value={exposureCap}
              onChange={(e) => setExposureCap(Number(e.target.value))}
              className="mt-2 w-full accent-sky-400"
              aria-label="Portfolio exposure cap"
            />
            <div className="mt-1 flex justify-between text-[10px] text-gray-600">
              <span>1.0u</span>
              <span>6.0u</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wide text-gray-500">Projected system behavior</p>
          <div className="space-y-2 text-xs">
            {[
              { label: 'Recommendation volume', value: `~${recVolume} / slate`, tone: 'text-sky-300' },
              { label: 'Confidence threshold', value: `≥ ${confidenceThreshold}`, tone: 'text-emerald-300' },
              {
                label: 'Estimated pass rate',
                value: `${passRate}%`,
                tone: passRate >= 80 ? 'text-emerald-300' : passRate >= 70 ? 'text-amber-300' : 'text-red-300',
              },
              {
                label: 'Estimated block rate',
                value: `${blockRate}%`,
                tone: blockRate <= 15 ? 'text-amber-300' : 'text-red-300',
              },
              { label: 'Max exposure / slate', value: `${exposureCap.toFixed(1)}u`, tone: 'text-blue-300' },
            ].map((row) => (
              <div
                key={row.label}
                className={[
                  'flex items-center justify-between rounded border border-surface-4 bg-surface-2 px-3 py-2',
                  !disableMotion ? 'transition-all duration-500' : '',
                ].join(' ')}
              >
                <span className="text-gray-400">{row.label}</span>
                <span className={`font-semibold tabular-nums ${row.tone}`}>{row.value}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-gray-600">
            Simulated values only — actual outputs depend on live market conditions and model state.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── New hook: viewport-triggered count-up ────────────────────────────────────

function useCountUp(target: number, decimals: number, duration: number, disableMotion: boolean) {
  const factor = Math.pow(10, decimals);
  const [count, setCount] = useState(disableMotion ? target : 0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disableMotion) {
      setCount(target);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        let startTime: number | null = null;
        const animate = (ts: number) => {
          if (!startTime) startTime = ts;
          const progress = Math.min((ts - startTime) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.round(target * eased * factor) / factor);
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, decimals, duration, disableMotion, factor]);

  return { count, ref };
}

// ─── New sub-components ───────────────────────────────────────────────────────

function HeroStatCounter({
  value,
  decimals,
  prefix,
  suffix,
  label,
  color,
  disableMotion,
}: {
  value: number;
  decimals: number;
  prefix: string;
  suffix: string;
  label: string;
  color: string;
  disableMotion: boolean;
}) {
  const factor = Math.pow(10, decimals);
  const [display, setDisplay] = useState(disableMotion ? value : 0);

  useEffect(() => {
    if (disableMotion) return;
    let startTime: number | null = null;
    const duration = 1800;
    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(value * eased * factor) / factor);
      if (progress < 1) requestAnimationFrame(animate);
    };
    const id = setTimeout(() => requestAnimationFrame(animate), 700);
    return () => clearTimeout(id);
  }, [value, decimals, disableMotion, factor]);

  const formatted = decimals > 0 ? display.toFixed(decimals) : Math.round(display).toString();

  return (
    <div className="flex flex-col items-center lg:items-start">
      <p className={`text-xl font-bold tabular-nums tracking-tight sm:text-2xl ${color}`}>
        {prefix}
        {formatted}
        {suffix}
      </p>
      <p className="mt-0.5 text-[11px] text-gray-500">{label}</p>
    </div>
  );
}

function MetricsTicker({ disableMotion }: { disableMotion: boolean }) {
  const doubled = [...tickerItems, ...tickerItems];

  if (disableMotion) {
    return (
      <div className="border-y border-white/8 bg-slate-950/70 py-3">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-x-8 gap-y-1 px-4 text-xs">
          {tickerItems.map((item) => (
            <span key={item.label} className="inline-flex items-center gap-2">
              <span className="text-gray-500">{item.label}</span>
              <span className={`font-semibold tabular-nums ${item.color}`}>{item.value}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden border-y border-white/8 bg-slate-950/70 py-3" aria-hidden>
      <div className="animate-ticker flex whitespace-nowrap">
        {doubled.map((item, i) => (
          <span key={i} className="inline-flex shrink-0 items-center gap-2.5 px-6 text-xs">
            <span className="h-1 w-1 rounded-full bg-white/15" />
            <span className="text-gray-500">{item.label}</span>
            <span className={`font-semibold tabular-nums ${item.color}`}>{item.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function ProblemSolution({ disableMotion }: { disableMotion: boolean }) {
  return (
    <motion.section
      className="mt-24"
      initial={disableMotion ? undefined : 'hidden'}
      whileInView={disableMotion ? undefined : 'show'}
      viewport={{ once: true, amount: 0.15 }}
      variants={sectionMotion}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center">
        <p className="text-xs font-medium uppercase tracking-wider text-brand">Why Navyrx</p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
          Most platforms sell picks.
          <br />
          <span className="text-gradient-cinematic">We validate every one.</span>
        </h2>
        <p className="mt-4 mx-auto max-w-2xl text-gray-400">
          The difference between a disciplined operator and a recreational gambler is process.
          Navyrx makes process the product.
        </p>
      </div>

      <div className="mt-10 grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-red-400/20 bg-red-400/5 p-6">
          <div className="mb-5 flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-red-400/60" />
            <p className="text-xs font-medium uppercase tracking-wider text-red-300/80">The old way</p>
          </div>
          <div className="space-y-2">
            {problemRows.map((row) => (
              <div
                key={row.old}
                className="flex items-center gap-3 rounded-lg border border-red-400/15 bg-red-400/5 px-4 py-3 text-sm"
              >
                <X className="h-4 w-4 shrink-0 text-red-400/70" />
                <span className="text-gray-400">{row.old}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/5 p-6">
          <div className="mb-5 flex items-center gap-2">
            <span className="signal-pulse relative inline-block h-2 w-2 rounded-full bg-emerald-400" />
            <p className="text-xs font-medium uppercase tracking-wider text-emerald-300/80">The Navyrx way</p>
          </div>
          <div className="space-y-2">
            {problemRows.map((row) => (
              <div
                key={row.nav}
                className="flex items-center gap-3 rounded-lg border border-emerald-400/20 bg-emerald-400/5 px-4 py-3 text-sm"
              >
                <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                <span className="text-gray-200">{row.nav}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function AgentsShowcase({ disableMotion }: { disableMotion: boolean }) {
  return (
    <motion.section
      className="mt-24"
      initial={disableMotion ? undefined : 'hidden'}
      whileInView={disableMotion ? undefined : 'show'}
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionMotion}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center">
        <p className="text-xs font-medium uppercase tracking-wider text-brand">Multi-agent intelligence</p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">8 Agents. One Validated Release.</h2>
        <p className="mt-4 mx-auto max-w-2xl text-gray-400">
          Every pick runs through 8 independent scoring agents. Each one votes. The Validation Agent
          challenges every consensus. Only clean signals get released — anything else is blocked with a full audit.
        </p>
      </div>

      <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {agentsData.map((agent, i) => (
          <motion.div
            key={agent.name}
            initial={disableMotion ? undefined : { opacity: 0, y: 20 }}
            whileInView={disableMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: disableMotion ? 0 : i * 0.07 }}
            className={`cinematic-card rounded-xl border p-5 ${agent.borderColor} ${agent.bgColor}`}
          >
            <div className="flex items-start justify-between">
              <div className={`rounded-lg border p-2 ${agent.borderColor} bg-slate-950/60`}>
                <agent.icon className={`h-4 w-4 ${agent.textColor}`} />
              </div>
              {agent.weight !== null && (
                <span className={`text-xs font-bold tabular-nums ${agent.textColor}`}>{agent.weight}%</span>
              )}
            </div>
            <h3 className="mt-3 text-sm font-semibold">{agent.name}</h3>
            <p className="mt-1 text-xs leading-relaxed text-gray-400">{agent.desc}</p>
            <div className="mt-4 flex items-center justify-between">
              <span className={`font-mono text-xs tabular-nums ${agent.signalTone}`}>{agent.signal}</span>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${agent.stateTone}`}>
                {agent.state}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-5 rounded-xl border border-surface-4 bg-surface-1/60 px-5 py-4 text-center text-sm">
        <span className="font-medium text-gray-300">6 agents agree · 1 disagrees · Risk agent issued BLOCK override</span>
        <span className="mx-3 text-gray-600">→</span>
        <span className="font-medium text-red-300">Recommendation blocked. Audit trail generated and stored.</span>
      </div>
    </motion.section>
  );
}

function HowItWorks({ disableMotion }: { disableMotion: boolean }) {
  return (
    <motion.section
      className="mt-24"
      initial={disableMotion ? undefined : 'hidden'}
      whileInView={disableMotion ? undefined : 'show'}
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionMotion}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center">
        <p className="text-xs font-medium uppercase tracking-wider text-brand">The process</p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">How Every Pick Gets Processed</h2>
        <p className="mt-4 mx-auto max-w-xl text-gray-400">
          No pick exits the system without passing every gate. Here's what happens from data ingest to final decision.
        </p>
      </div>

      <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-surface-4 lg:grid-cols-3 lg:gap-0">
        {howItWorksSteps.map((step, i) => (
          <motion.div
            key={step.num}
            initial={disableMotion ? undefined : { opacity: 0, y: 16 }}
            whileInView={disableMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: disableMotion ? 0 : i * 0.14 }}
            className="relative overflow-hidden bg-surface-1 px-8 py-10"
          >
            <div className="pointer-events-none absolute right-4 top-2 select-none text-8xl font-black text-white/[0.025]">
              {step.num}
            </div>
            {i < 2 && (
              <div
                aria-hidden
                className="absolute right-0 top-1/2 hidden h-8 w-px -translate-y-1/2 bg-gradient-to-b from-transparent via-white/10 to-transparent lg:block"
              />
            )}
            <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl border ${step.borderColor} ${step.bgColor}`}>
              <step.icon className={`h-5 w-5 ${step.color}`} />
            </div>
            <p className={`mt-3 text-[10px] font-bold uppercase tracking-widest ${step.color}`}>Step {step.num}</p>
            <h3 className="mt-1.5 text-lg font-semibold">{step.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-gray-400">{step.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="glass-panel mt-6 rounded-2xl p-5">
        <p className="text-xs text-gray-400">Recommendation lifecycle stages</p>
        <div className="mt-4 overflow-x-auto pb-1">
          <div
            role="list"
            aria-label="Recommendation lifecycle stages"
            className="flex min-w-max items-start gap-0"
            data-testid="lifecycle-flow-diagram"
          >
            {lifecycleStages.map((item, i) => (
              <div key={item.step} role="listitem" className="flex items-center">
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    aria-label={
                      item.state === 'done'
                        ? `${item.step}: done`
                        : item.state === 'gate'
                          ? `${item.step}: validation gate`
                          : item.state === 'active'
                            ? `${item.step}: active`
                            : `${item.step}: pending`
                    }
                    className={[
                      'relative flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-bold',
                      item.state === 'done'
                        ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-300'
                        : item.state === 'gate'
                          ? 'signal-pulse border-sky-400/70 bg-sky-500/20 text-sky-200'
                          : item.state === 'active'
                            ? 'border-blue-400/70 bg-blue-500/20 text-blue-300'
                            : 'border-white/10 bg-surface-2/40 text-gray-600',
                    ].join(' ')}
                  >
                    <span aria-hidden>
                      {item.state === 'done' ? '✓' : item.state === 'gate' ? '⬡' : item.state === 'active' ? '◉' : '○'}
                    </span>
                  </div>
                  <span
                    className={[
                      'w-10 text-center text-[10px] font-medium',
                      item.state === 'done'
                        ? 'text-emerald-300/80'
                        : item.state === 'gate'
                          ? 'text-sky-200'
                          : item.state === 'active'
                            ? 'text-blue-300'
                            : 'text-gray-600',
                    ].join(' ')}
                  >
                    {item.step}
                  </span>
                </div>
                {i < lifecycleStages.length - 1 && (
                  <div
                    aria-hidden
                    className={[
                      'mb-4 h-px w-5 flex-shrink-0',
                      i < 2 ? 'bg-emerald-400/35' : i === 2 ? 'bg-sky-400/30' : 'bg-white/10',
                    ].join(' ')}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-1 flex items-center justify-between text-[10px] text-gray-600">
          <span>Validate gate: evidence + uncertainty check</span>
          <span className="text-sky-400/60">→ Release / Block</span>
        </div>
      </div>
    </motion.section>
  );
}

function CountUpKpiCard({
  kpi,
  animValue,
  disableMotion,
}: {
  kpi: (typeof institutionalKPIs)[number];
  animValue: (typeof kpiAnimationValues)[number];
  disableMotion: boolean;
}) {
  const { count, ref } = useCountUp(animValue.raw, animValue.decimals, 1800, disableMotion);
  const formatted = animValue.decimals > 0 ? count.toFixed(animValue.decimals) : Math.round(count).toString();

  return (
    <div ref={ref} className="cinematic-card rounded-xl border border-surface-4 bg-surface-1 p-5">
      <div className="flex items-center gap-2">
        <kpi.icon className={`h-4 w-4 ${kpi.tone}`} />
        <p className="text-xs text-gray-400">{kpi.label}</p>
      </div>
      <p className={`mt-3 text-2xl font-semibold tabular-nums ${kpi.tone}`}>
        {animValue.prefix}
        {formatted}
        {animValue.suffix}
      </p>
      <p className="mt-1 text-xs text-gray-500">{kpi.sub}</p>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function MarketingHomePage() {
  const reduced = useReducedMotion();
  const disableMotion = !!reduced;

  usePageMeta({
    title: '',
    description:
      'Navyrx Sports presents Navyrx Pulse: a premium multi-agent sports intelligence command center with transparent validation and portfolio risk controls.',
    path: '/',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Navyrx Sports',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'AI-powered sports intelligence and decision-support platform with explainable recommendations and validation workflows.',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
  });

  return (
    <>
      {/* ── Mobile sticky CTA ─────────────────────────────────────────────── */}
      <div className="mobile-cta-stable fixed inset-x-0 bottom-0 z-30 border-t border-brand/20 bg-surface-0/95 p-3 backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-7xl items-center gap-2">
          <Link to={MARKETING_CTA.requestAccess.to} className="btn-primary flex-1 text-center">
            {MARKETING_CTA.requestAccess.label}
          </Link>
          <Link to={MARKETING_CTA.comparePlans.to} className="btn-ghost border border-surface-4 px-3">
            {MARKETING_CTA.comparePlans.label}
          </Link>
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-7xl px-4 pt-14 sm:px-6 lg:px-8">
        <motion.section
          initial={reduced ? undefined : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="relative overflow-hidden rounded-3xl border border-slate-200/15 bg-gradient-to-br from-slate-950 via-slate-900/98 to-slate-950 px-6 py-10 lg:px-10 lg:py-12"
        >
          {/* Ambient grid */}
          <div className="pointer-events-none absolute inset-0 ambient-grid opacity-30" />
          <div className="pointer-events-none absolute inset-0 ambient-grid-animated opacity-20" />
          <div className="pointer-events-none absolute inset-0 cinematic-vignette" />

          {/* Ambient blobs */}
          <motion.div
            aria-hidden
            animate={disableMotion ? undefined : { x: ['0%', '10%', '0%'], y: ['0%', '-8%', '0%'] }}
            transition={disableMotion ? undefined : { duration: 16, repeat: Infinity, ease: 'easeInOut' }}
            className="hero-ambient-blob pointer-events-none absolute -right-32 top-[-10rem] h-[28rem] w-[28rem] rounded-full bg-sky-500/25 blur-3xl"
          />
          <motion.div
            aria-hidden
            animate={disableMotion ? undefined : { x: ['0%', '-8%', '0%'], y: ['0%', '8%', '0%'] }}
            transition={disableMotion ? undefined : { duration: 18, repeat: Infinity, ease: 'easeInOut' }}
            className="hero-ambient-blob pointer-events-none absolute -bottom-24 left-[-6rem] h-[22rem] w-[22rem] rounded-full bg-indigo-500/25 blur-3xl"
          />
          <motion.div
            aria-hidden
            animate={disableMotion ? undefined : { x: ['0%', '5%', '-5%', '0%'], y: ['0%', '6%', '-4%', '0%'] }}
            transition={disableMotion ? undefined : { duration: 22, repeat: Infinity, ease: 'easeInOut' }}
            className="hero-ambient-blob pointer-events-none absolute left-[40%] top-[-4rem] h-64 w-64 rounded-full bg-blue-600/20 blur-3xl"
          />
          <motion.div
            aria-hidden
            animate={disableMotion ? undefined : { x: ['0%', '-6%', '0%'], y: ['0%', '-10%', '0%'] }}
            transition={disableMotion ? undefined : { duration: 20, repeat: Infinity, ease: 'easeInOut' }}
            className="hero-ambient-blob pointer-events-none absolute -bottom-16 right-[10%] h-48 w-48 rounded-full bg-cyan-500/20 blur-3xl"
          />

          {/* Signal bridge beam */}
          <div aria-hidden className="pointer-events-none absolute left-8 right-8 hidden lg:block" style={{ top: '5.5rem' }}>
            <div className={['h-px w-full', disableMotion ? 'signal-bridge-static' : 'telemetry-line-fast'].join(' ')} />
          </div>

          <div className="relative grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
            {/* Left column */}
            <div>
              {/* PulseCoreVisual — desktop anchor */}
              <div className="mb-6 hidden lg:block">
                <PulseCoreVisual disableMotion={disableMotion} />
              </div>

              {/* Eyebrow */}
              <p className="glass-panel inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium text-sky-200">
                <Sparkles className="h-3.5 w-3.5" /> {EARLY_ACCESS_BADGE.label} · {EARLY_ACCESS_BADGE.detail}
              </p>

              {/* NEW punchy headline */}
              <h1 className="mt-5">
                <span className="block text-gradient-cinematic text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                  Your Edge Starts
                </span>
                <span className="block text-4xl font-bold leading-tight tracking-tight text-gray-200/90 sm:text-5xl lg:text-6xl">
                  With What We Block.
                </span>
              </h1>

              <p className="mt-5 max-w-xl text-base leading-relaxed text-gray-300">
                Navyrx Pulse runs 8 parallel agents against every pick — validating, blocking, and releasing with full transparency.
                Most platforms tell you what to bet. We tell you what <em className="not-italic text-sky-200">not</em> to bet, and why.
              </p>

              {/* CTAs */}
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to={MARKETING_CTA.requestAccess.to}>
                  <motion.div
                    whileHover={{ y: -2, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="group relative overflow-hidden rounded-2xl px-6 py-4 bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-600 text-white shadow-[0_8px_30px_rgba(0,123,255,0.35)] border border-white/10 backdrop-blur-xl transition-all duration-300"
                  >
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.18),transparent_65%)]" />
                    <div className="absolute inset-y-0 -left-1/2 w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/20 to-transparent blur-md transition-all duration-1000 group-hover:left-[140%]" />
                    <div className="relative z-10 flex items-center gap-3">
                      <motion.div
                        animate={{ y: [0, -2, 0] }}
                        transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                        className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 border border-white/10 backdrop-blur-md"
                      >
                        <Rocket className="h-5 w-5" />
                      </motion.div>
                      <div className="flex flex-col items-start leading-none">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-white/70 font-semibold mb-1">
                          Early Access
                        </span>
                        <span className="text-base font-bold tracking-tight">Request Access</span>
                      </div>
                    </div>
                  </motion.div>
                </Link>
                <Link to="/how-it-works" className="glass-panel btn-ghost border border-white/15 px-5 py-2.5">
                  Explore Navyrx Pulse
                </Link>
                <Link to="/features" className="glass-panel btn-ghost border border-white/15 px-5 py-2.5">
                  See Validation Engine
                </Link>
              </div>

              {/* Animated hero stat counters */}
              <div className="mt-7 grid grid-cols-3 divide-x divide-white/10 overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm">
                {heroStatValues.map((stat) => (
                  <div key={stat.label} className="px-4 py-3">
                    <HeroStatCounter {...stat} disableMotion={disableMotion} />
                  </div>
                ))}
              </div>

              {/* Feature bullets */}
              <div className="mt-5 flex flex-wrap gap-5 text-xs text-gray-400">
                <span className="inline-flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-brand" /> Multi-agent orchestration
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Workflow className="h-3.5 w-3.5 text-brand" /> Recommendation lifecycle
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-brand" /> Trust-first validation
                </span>
              </div>
            </div>

            {/* Right column — live command center preview */}
            <motion.div
              animate={
                disableMotion
                  ? undefined
                  : {
                      opacity: [0.95, 1, 0.96],
                      y: [0, -6, 0],
                    }
              }
              transition={disableMotion ? undefined : { repeat: Infinity, duration: 5, ease: 'easeInOut' }}
              className="glass-panel rounded-2xl p-5"
              data-testid="hero-command-center"
            >
              <p className="text-[11px] text-gray-500">Illustrative Navyrx Pulse preview — product narrative mockup.</p>
              <div className="mt-3 overflow-hidden rounded-lg border border-white/10 bg-slate-900/80 p-3">
                <div className="telemetry-line h-[1px] w-full" />
                <div className="mt-1.5 telemetry-line-fast h-px w-2/3 opacity-60" />
                <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                  {telemetryReadout.map((item) => (
                    <div key={item.metric} className="rounded border border-white/10 bg-slate-950/80 px-2 py-1">
                      <p className="text-gray-400">{item.metric}</p>
                      <p className={`mt-1 font-medium tabular-nums ${item.tone}`}>{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <LivePulseBoard disableMotion={disableMotion} />
              </div>

              <div className="mt-4">
                <AgentOrchestrationPanel disableMotion={disableMotion} />
              </div>

              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <div className="glass-panel rounded-lg p-4">
                  <p className="text-xs text-gray-400">Slate clustering</p>
                  <div className="mt-2 space-y-2 text-xs">
                    <div className="rounded border border-white/10 bg-slate-950/80 px-2 py-1">Early Slate · 4 games</div>
                    <div className="rounded border border-white/10 bg-slate-950/80 px-2 py-1">Prime Slate · 7 games</div>
                    <div className="rounded border border-white/10 bg-slate-950/80 px-2 py-1">Late Slate · 3 games</div>
                  </div>
                </div>
                <div className="glass-elevated rounded-lg p-4">
                  <p className="text-xs text-red-200">Blocked recommendation</p>
                  <p className="mt-2 text-sm font-medium">NYM ML +134 blocked</p>
                  <p className="mt-1 text-xs text-red-100/80">
                    Validation rejected: sharp movement divergence and uncertainty above release threshold.
                  </p>
                </div>
              </div>

              <div className="mt-3 rounded-lg border border-white/10 bg-slate-950/70 p-4">
                <p className="text-xs text-gray-400">Validation lock timeline</p>
                <ol aria-label="Validation lock timeline events" className="mt-2 space-y-2 text-xs text-gray-300">
                  {commandTimeline.map(([label, stamp]) => (
                    <li key={label} className="flex items-center justify-between rounded border border-white/10 bg-slate-900/70 px-2 py-1.5">
                      <span className="inline-flex items-center gap-2">
                        <span className="signal-pulse relative inline-block h-2 w-2 rounded-full bg-sky-300" />
                        {label}
                      </span>
                      <span className="font-mono text-[11px] text-sky-200">{stamp}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </motion.div>
          </div>
        </motion.section>
      </div>

      {/* ── Metrics Ticker — full-width ───────────────────────────────────── */}
      <div className="mt-10">
        <MetricsTicker disableMotion={disableMotion} />
      </div>

      {/* ── Remaining sections ────────────────────────────────────────────── */}
      <div className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">

        {/* Problem / Solution */}
        <ProblemSolution disableMotion={disableMotion} />

        {/* 8 Agents showcase */}
        <AgentsShowcase disableMotion={disableMotion} />

        {/* How It Works */}
        <HowItWorks disableMotion={disableMotion} />

        {/* ── Trust Engine ─────────────────────────────────────────────────── */}
        <motion.section
          className="mt-24"
          initial={reduced ? undefined : 'hidden'}
          whileInView={reduced ? undefined : 'show'}
          viewport={{ once: true, amount: 0.15 }}
          variants={sectionMotion}
          transition={{ duration: 0.45 }}
        >
          <p className="text-xs font-medium uppercase tracking-wider text-brand">Governance</p>
          <h2 className="mt-3 text-2xl font-semibold sm:text-3xl">Trust Engine</h2>
          <p className="mt-3 max-w-3xl text-gray-400">
            Institutional process over gambling hype: every recommendation carries evidence, validation
            decisions, uncertainty context, and risk controls.
          </p>

          <div className="mt-6 rounded-2xl border border-surface-4 bg-surface-1/50 p-5" data-testid="trust-gate-diagram">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Control flow</p>
            <div className="mt-4 flex flex-col items-center gap-3 text-xs">
              <div className="w-full max-w-xs rounded-lg border border-white/10 bg-slate-900/80 px-5 py-2.5 text-center">
                <p className="text-[10px] uppercase tracking-wide text-gray-500">Incoming</p>
                <p className="font-semibold text-gray-200">Recommendation Candidate</p>
              </div>
              <div aria-hidden className="h-5 w-px bg-gradient-to-b from-sky-400/50 to-sky-400/20" />
              <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { label: 'Evidence', sub: 'Packet review' },
                  { label: 'Validation', sub: 'Gate checks', active: true },
                  { label: 'Uncertainty', sub: 'Confidence scoring' },
                  { label: 'Risk Controls', sub: 'Exposure check' },
                ].map((gate) => (
                  <div
                    key={gate.label}
                    className={[
                      'rounded-lg border px-2 py-2.5 text-center',
                      gate.active
                        ? 'border-sky-400/40 bg-sky-400/10 text-sky-200'
                        : 'border-white/10 bg-slate-900/60 text-gray-400',
                    ].join(' ')}
                  >
                    <p className="text-[10px] font-medium">{gate.label}</p>
                    <p className="mt-0.5 text-[9px] opacity-70">{gate.sub}</p>
                  </div>
                ))}
              </div>
              <div aria-hidden className="h-5 w-px bg-gradient-to-b from-sky-400/20 to-transparent" />
              <div className="grid w-full max-w-xs grid-cols-2 gap-3">
                <div className="rounded-lg border border-emerald-400/35 bg-emerald-400/10 px-3 py-2.5 text-center">
                  <p className="text-[10px] font-bold text-emerald-300">✓ Release</p>
                  <p className="mt-0.5 text-[9px] text-emerald-300/70">All gates passed</p>
                </div>
                <div className="rounded-lg border border-red-400/35 bg-red-400/10 px-3 py-2.5 text-center">
                  <p className="text-[10px] font-bold text-red-300">✕ Block</p>
                  <p className="mt-0.5 text-[9px] text-red-300/70">Gate threshold exceeded</p>
                </div>
              </div>
            </div>
          </div>

          <ol className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {trustPillars.map((pillar, i) => (
              <motion.li
                key={pillar.title}
                initial={disableMotion ? undefined : { opacity: 0, y: 16 }}
                whileInView={disableMotion ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: disableMotion ? 0 : i * 0.1, duration: 0.4 }}
                className="rounded-xl border border-surface-4 bg-surface-1 p-5"
              >
                <div className="flex items-center gap-2">
                  <pillar.icon className="h-4 w-4 text-brand" />
                  <p className="text-xs text-brand">0{i + 1}</p>
                </div>
                <h3 className="mt-3 text-sm font-semibold">{pillar.title}</h3>
                <ul className="mt-2 space-y-1 text-xs text-gray-300">
                  {pillar.points.map((point) => (
                    <li key={point}>• {point}</li>
                  ))}
                </ul>
              </motion.li>
            ))}
          </ol>

          <div className="mt-6 grid gap-3 lg:grid-cols-2">
            <div className="rounded-xl border border-surface-4 bg-surface-1 p-5">
              <p className="text-xs text-gray-400">Validation pipeline</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                {[
                  ['Data integrity', 'Pass'],
                  ['Model divergence', 'Watch'],
                  ['Line movement sanity', 'Pass'],
                  ['Risk concentration', 'Blocked'],
                ].map(([name, status]) => (
                  <div key={name} className="rounded border border-surface-4 bg-surface-2 px-3 py-2">
                    <p className="text-gray-300">{name}</p>
                    <p className="mt-1 text-brand">{status}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-surface-4 bg-surface-1 p-5">
              <p className="text-xs text-gray-400">Market movement + audit trail</p>
              <div className="mt-3 space-y-2 text-xs text-gray-300">
                <div className="flex items-center justify-between rounded border border-surface-4 bg-surface-2 px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    <LineChart className="h-3.5 w-3.5 text-brand" /> Line shift alert
                  </span>
                  <span>-1.5 → -2.0</span>
                </div>
                <div className="flex items-center justify-between rounded border border-surface-4 bg-surface-2 px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    <Database className="h-3.5 w-3.5 text-brand" /> Evidence packet
                  </span>
                  <span>Attached</span>
                </div>
                <div className="flex items-center justify-between rounded border border-surface-4 bg-surface-2 px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    <Flag className="h-3.5 w-3.5 text-brand" /> Exposure warning
                  </span>
                  <span>Prime slate cap hit</span>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* ── Product Ecosystem ─────────────────────────────────────────────── */}
        <section className="glass-panel relative mt-20 overflow-hidden rounded-2xl p-6">
          <motion.div
            aria-hidden
            animate={disableMotion ? undefined : { x: ['0%', '8%', '0%'], opacity: [0.6, 1, 0.6] }}
            transition={disableMotion ? undefined : { duration: 14, repeat: Infinity, ease: 'easeInOut' }}
            className="hero-ambient-blob pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-brand/20 blur-3xl"
          />
          <motion.div
            aria-hidden
            animate={disableMotion ? undefined : { x: ['0%', '-6%', '0%'], opacity: [0.5, 0.9, 0.5] }}
            transition={disableMotion ? undefined : { duration: 17, repeat: Infinity, ease: 'easeInOut' }}
            className="hero-ambient-blob pointer-events-none absolute -bottom-16 left-0 h-48 w-48 rounded-full bg-sky-500/15 blur-3xl"
          />

          <p className="relative text-xs font-medium uppercase tracking-wider text-brand">Platform</p>
          <h2 className="relative mt-2 text-2xl font-semibold">Product Ecosystem Architecture</h2>
          <p className="mt-3 max-w-3xl text-gray-400">
            Navyrx Sports provides the institutional platform, Pulse is the command center, and each
            Edge engine specializes by sport while sharing trust, risk, and validation infrastructure.
          </p>

          <div className="mt-6 flex flex-col items-center gap-0">
            <div className="glass-elevated w-full max-w-xs rounded-xl px-6 py-3 text-center">
              <p className="text-[10px] uppercase tracking-wide text-brand">Platform</p>
              <p className="text-lg font-semibold">Navyrx Sports</p>
              <p className="mt-1 text-xs text-gray-400">Institutional AI sports intelligence platform</p>
            </div>

            <div aria-hidden className="h-6 w-px bg-gradient-to-b from-brand/50 to-sky-400/50" />

            <div
              className="glass-elevated relative w-full max-w-sm rounded-xl border border-sky-400/30 px-8 py-4 text-center"
              aria-label="Pulse: active command center"
            >
              <span aria-hidden className="signal-pulse absolute right-4 top-4 inline-block h-2 w-2 rounded-full bg-sky-400" />
              <p className="text-[10px] uppercase tracking-wide text-sky-300">Command Center</p>
              <p className="text-2xl font-bold text-sky-100">Pulse</p>
              <p className="mt-1 text-xs text-gray-300">Agent orchestration · Risk controls · Trust lifecycle</p>
            </div>

            <div className="my-3 flex w-full items-center gap-2">
              <div aria-hidden className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
              <p className="px-2 text-[10px] text-gray-600">Shared Trust · Risk · Validation Infrastructure</p>
              <div aria-hidden className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
            </div>

            <div className="mt-1 grid w-full gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {ecosystemModules.map((module) => (
                <Link
                  key={module.product}
                  to={module.to}
                  aria-label={`View ${module.product} details`}
                  className="group glass-panel cinematic-card block rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg" aria-hidden>{module.emoji}</span>
                      <h3 className="font-semibold">{module.product}</h3>
                    </div>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${module.statusColor}`}>
                      {module.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-400">{module.sport} intelligence engine</p>
                  <p className="mt-2 flex items-center gap-1 text-[11px] text-brand opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    View details <ArrowRight className="h-3 w-3" />
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── Operational KPIs with counter animations ─────────────────────── */}
        <motion.section
          className="mt-20"
          initial={reduced ? undefined : 'hidden'}
          whileInView={reduced ? undefined : 'show'}
          viewport={{ once: true, amount: 0.1 }}
          variants={sectionMotion}
          transition={{ duration: 0.45 }}
          data-testid="institutional-metrics"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-brand">Metrics</p>
              <h2 className="mt-2 text-2xl font-semibold sm:text-3xl">Operational Governance Metrics</h2>
              <p className="mt-3 max-w-3xl text-gray-400">
                Institutional rigor tracked across every ingest cycle. Transparency into how the
                system performs — not just outputs, but the discipline behind them.
              </p>
            </div>
            <p className="text-[10px] text-gray-600">Simulated operational metrics</p>
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {institutionalKPIs.map((kpi, i) => (
              <CountUpKpiCard
                key={kpi.label}
                kpi={kpi}
                animValue={kpiAnimationValues[i]}
                disableMotion={disableMotion}
              />
            ))}
          </div>
        </motion.section>

        {/* ── Simulation Mode ───────────────────────────────────────────────── */}
        <motion.section
          className="mt-20"
          initial={reduced ? undefined : 'hidden'}
          whileInView={reduced ? undefined : 'show'}
          viewport={{ once: true, amount: 0.1 }}
          variants={sectionMotion}
          transition={{ duration: 0.45 }}
        >
          <p className="text-xs font-medium uppercase tracking-wider text-brand">Interactive</p>
          <h2 className="mt-2 text-2xl font-semibold sm:text-3xl">Simulate Your Decision Environment</h2>
          <p className="mt-3 max-w-3xl text-gray-400">
            Configure operational parameters and see how Pulse adapts its recommendation volume,
            confidence thresholds, and risk controls to your operating profile.
          </p>
          <SimulationModePanel disableMotion={disableMotion} />
        </motion.section>

        {/* ── Controlled Access Paths ───────────────────────────────────────── */}
        <section className="mt-20" id="pricing">
          <h2 className="text-2xl font-semibold sm:text-3xl">Controlled Access Paths</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[
              {
                tier: 'Founding Cohort',
                cta: MARKETING_CTA.requestAccess.label,
                to: MARKETING_CTA.requestAccess.to,
                notes: ['Submit institutional access request', 'Manual review for onboarding wave', 'Calibrated rollout by cohort'],
                featured: true,
              },
              {
                tier: 'Validation Engine',
                cta: MARKETING_CTA.validationEngine.label,
                to: MARKETING_CTA.validationEngine.to,
                notes: ['Evidence and audit trail cards', 'Blocked recommendation examples', 'Confidence and uncertainty context'],
                featured: false,
              },
              {
                tier: 'Plans',
                cta: MARKETING_CTA.comparePlans.label,
                to: MARKETING_CTA.comparePlans.to,
                notes: ['Tier comparison and best-fit guidance', 'Enterprise roadmap positioning', 'ROI and process value framing'],
                featured: false,
              },
            ].map((plan) => (
              <article
                key={plan.tier}
                className={[
                  'relative rounded-xl p-5',
                  plan.featured
                    ? 'glass-elevated border border-brand/30'
                    : 'glass-panel',
                ].join(' ')}
              >
                {plan.featured && (
                  <span className="absolute -top-2.5 left-4 rounded-full border border-brand/50 bg-brand/20 px-3 py-0.5 text-[10px] font-medium text-sky-200 uppercase tracking-wide">
                    Primary
                  </span>
                )}
                <h3 className="text-lg font-semibold">{plan.tier}</h3>
                <ul className="mt-3 space-y-2 text-sm text-gray-300">
                  {plan.notes.map((note) => (
                    <li key={note} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
                      {note}
                    </li>
                  ))}
                </ul>
                <Link to={plan.to} className="cinematic-cta mt-5">
                  {plan.cta}
                </Link>
              </article>
            ))}
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────────────── */}
        <section className="mt-20">
          <h2 className="text-2xl font-semibold sm:text-3xl">FAQ</h2>
          <div className="mt-6 grid gap-2">
            {faqs.map((faq) => (
              <details key={faq.q} className="group rounded-xl border border-surface-4 bg-surface-1 p-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
                  <span>{faq.q}</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-gray-300">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── Final CTA ─────────────────────────────────────────────────────── */}
        <section className="glass-elevated relative mb-8 mt-20 overflow-hidden rounded-2xl p-10 text-center">
          <motion.div
            aria-hidden
            animate={disableMotion ? undefined : { x: ['0%', '12%', '0%'], y: ['0%', '-8%', '0%'], opacity: [0.5, 0.9, 0.5] }}
            transition={disableMotion ? undefined : { duration: 15, repeat: Infinity, ease: 'easeInOut' }}
            className="hero-ambient-blob pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand/25 blur-3xl"
          />
          <motion.div
            aria-hidden
            animate={disableMotion ? undefined : { x: ['0%', '-10%', '0%'], y: ['0%', '10%', '0%'], opacity: [0.4, 0.8, 0.4] }}
            transition={disableMotion ? undefined : { duration: 18, repeat: Infinity, ease: 'easeInOut' }}
            className="hero-ambient-blob pointer-events-none absolute -bottom-12 -left-12 h-48 w-48 rounded-full bg-indigo-500/20 blur-3xl"
          />

          <div className="relative">
            <p className="text-xs font-medium uppercase tracking-wider text-sky-300">Join the founding cohort</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Stop Betting on Instinct.
            </h2>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-gradient-cinematic sm:text-4xl lg:text-5xl">
              Start Operating with Intelligence.
            </h2>
            <p className="relative mt-5 mx-auto max-w-xl text-gray-300">
              Move from reactive picks to structured intelligence with explainable recommendations,
              validation rigor, and disciplined portfolio controls. No hype. No guarantees. Just process.
            </p>
            <div className="relative mt-7 flex flex-wrap justify-center items-center gap-3">
              <Link to={MARKETING_CTA.requestAccess.to}>
                <motion.div
                  whileHover={{ y: -2, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative overflow-hidden rounded-2xl px-6 py-4 bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-600 text-white shadow-[0_8px_30px_rgba(0,123,255,0.35)] border border-white/10 backdrop-blur-xl transition-all duration-300"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.18),transparent_65%)]" />
                  <div className="absolute inset-y-0 -left-1/2 w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/20 to-transparent blur-md transition-all duration-1000 group-hover:left-[140%]" />
                  <div className="relative z-10 flex items-center gap-3">
                    <motion.div
                      animate={{ y: [0, -2, 0] }}
                      transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                      className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 border border-white/10 backdrop-blur-md"
                    >
                      <Rocket className="h-5 w-5" />
                    </motion.div>
                    <div className="flex flex-col items-start leading-none">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-white/70 font-semibold mb-1">
                        Early Access
                      </span>
                      <span className="text-base font-bold tracking-tight">Request Access</span>
                    </div>
                  </div>
                </motion.div>
              </Link>
              <Link to={MARKETING_CTA.comparePlans.to} className="glass-panel btn-ghost border border-white/15 px-5 py-3">
                {MARKETING_CTA.comparePlans.label}
              </Link>
              <Link to={MARKETING_CTA.recommendationLifecycle.to} className="glass-panel btn-ghost border border-white/15 px-5 py-3">
                {MARKETING_CTA.recommendationLifecycle.label}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
